// src/client/lib/api-client.ts
import { API_BASE } from '../../client-config';
import type { ApiError } from '@client/types';

// ============================================================================
// Types
// ============================================================================

interface RequestConfig extends RequestInit {
  headers?: Record<string, string>;
}

// ============================================================================
// Logging Utilities (Development Only)
// ============================================================================

const isDev = false;

function logRequest(method: string, url: string): void {
  if (isDev) {
    console.log(`[API] ${method} ${url}`);
  }
}

function logResponse(status: number, statusText: string, url: string): void {
  if (isDev) {
    console.log(`[API] ${status} ${statusText} - ${url}`);
  }
}

function logError(message: string, error?: unknown): void {
  if (isDev) {
    console.error(`[API] ${message}`, error);
  }
}

function logSuccess(data: unknown): void {
  if (isDev) {
    console.log('[API] Success:', data);
  }
}

// ============================================================================
// Error Utilities
// ============================================================================

function createApiError(
  status: number,
  message: string,
  details?: unknown
): ApiError {
  return {
    status,
    message,
    details
  };
}

async function parseErrorResponse(response: Response): Promise<string> {
  const contentType = response.headers.get('content-type');
  let errorMessage = `HTTP ${response.status}: ${response.statusText}`;

  try {
    if (contentType?.includes('application/json')) {
      const errorData = await response.json();
      errorMessage = errorData.message || errorData.error || errorMessage;
    } else {
      const errorText = await response.text();
      if (errorText) {
        errorMessage = errorText;
      }
    }
  } catch (parseError) {
    logError('Failed to parse error response', parseError);
  }

  return errorMessage;
}



// ============================================================================
// ApiClient Class
// ============================================================================

export class ApiClient {
  private static readonly apiBase = API_BASE;

  /**
   * Core request method with session-based authentication
   * Automatically includes credentials (cookies) for all requests
   */
  private static async request<T>(
    endpoint: string,
    options: RequestConfig = {}
  ): Promise<T> {
    const url = `${this.apiBase}${endpoint}`;

    logRequest(options.method || 'GET', url);

    const config: RequestConfig = {
      ...options,
      headers: {
        'Content-Type': 'application/json',
        ...options.headers,
      },
      credentials: 'include', // CRITICAL: Send session cookies
    };

    try {
      const response = await fetch(url, config);

      logResponse(response.status, response.statusText, url);

      // Handle error responses
      if (!response.ok) {
        const errorMessage = await parseErrorResponse(response);
        logError(errorMessage);

        const apiError = createApiError(
          response.status,
          errorMessage,
          { url, method: options.method }
        );

        throw apiError;
      }

      // Parse successful response
      const contentType = response.headers.get('content-type');

      if (contentType?.includes('application/json')) {
        const data = await response.json();
        logSuccess(data);
        return data as T;
      } else {
        // Handle non-JSON responses (e.g., text, blob)
        const text = await response.text();
        return text as unknown as T;
      }
    } catch (error) {
      // Re-throw ApiError as-is
      if (this.isApiError(error)) {
        throw error;
      }

      // Wrap other errors
      if (error instanceof Error) {
        logError('Request failed', error);
        throw createApiError(0, error.message, error);
      }

      // Unknown error
      logError('Unknown error occurred', error);
      throw createApiError(0, 'Network request failed', error);
    }
  }

  // ============================================================================
  // Standard HTTP Methods
  // ============================================================================

  /**
   * GET request
   */
  static async get<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'GET' });
  }

  /**
   * POST request
   */
  static async post<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'POST',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * PUT request
   */
  static async put<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PUT',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  /**
   * DELETE request
   */
  static async delete<T>(endpoint: string): Promise<T> {
    return this.request<T>(endpoint, { method: 'DELETE' });
  }

  /**
   * PATCH request
   */
  static async patch<T>(endpoint: string, data?: unknown): Promise<T> {
    return this.request<T>(endpoint, {
      method: 'PATCH',
      body: data ? JSON.stringify(data) : undefined,
    });
  }

  // ============================================================================
  // Access Code Methods (for Userscripts)
  // ============================================================================

  /**
   * GET request with access code in query string
   * Used by userscripts and external API access
   */
  static async getWithAccessCode<T>(
    endpoint: string,
    accessCode: string
  ): Promise<T> {
    const separator = endpoint.includes('?') ? '&' : '?';
    const urlWithCode = `${endpoint}${separator}access_code=${accessCode}`;
    return this.get<T>(urlWithCode);
  }

  /**
   * POST request with access code in query string
   * Used by userscripts and external API access
   */
  static async postWithAccessCode<T>(
    endpoint: string,
    accessCode: string,
    data?: unknown
  ): Promise<T> {
    const separator = endpoint.includes('?') ? '&' : '?';
    const urlWithCode = `${endpoint}${separator}access_code=${accessCode}`;
    return this.post<T>(urlWithCode, data);
  }

  // ============================================================================
  // Utility Methods
  // ============================================================================

  /**
   * Type guard to check if error is ApiError
   */
  static isApiError(error: unknown): error is ApiError {
    return (
      typeof error === 'object' &&
      error !== null &&
      'status' in error &&
      'message' in error
    );
  }

  /**
   * Check authentication status (development helper)
   */
  static async debugAuth(): Promise<void> {
    if (!isDev) {
      console.warn('[API] debugAuth() is only available in development');
      return;
    }

    try {
      console.log('[API Debug] Checking authentication...');
      console.log('[API Debug] Base URL:', this.apiBase);
      console.log('[API Debug] Credentials: include (cookies enabled)');

      const response = await this.get<any>('/auth/status');
      console.log('[API Debug] Auth Status Response:', response);

      if (response.isAuthenticated) {
        console.log('[API Debug] User authenticated');
        console.log('[API Debug] User:', response.user);
      } else {
        console.log('[API Debug] User not authenticated');
      }
    } catch (error) {
      console.error('[API Debug] Auth check failed:', error);
    }
  }

  /**
   * Health check (development helper)
   */
  static async healthCheck(): Promise<unknown> {
    try {
      if (isDev) {
        console.log('[API] Checking server health...');
      }

      const response = await this.get<unknown>('/health');

      if (isDev) {
        console.log('[API] Server is healthy');
      }

      return response;
    } catch (error) {
      if (isDev) {
        console.error('[API] Health check failed:', error);
      }
      throw error;
    }
  }

  /**
   * Get API base URL (for debugging)
   */
  static getApiBase(): string {
    return this.apiBase;
  }
}

// ============================================================================
// Development-only exports
// ============================================================================

if (isDev) {
  // Expose ApiClient to window for console debugging
  (window as any).__apiClient = ApiClient;

  console.log('[ApiClient] Development mode enabled');
  console.log('[ApiClient] Access via window.__apiClient');
  console.log('[ApiClient] Base URL:', ApiClient.getApiBase());
}